import { createInvestmentProxyMetadataKey, extractHoldingsProxy } from 'venn-utils';
import { create, windowScheduler } from '@yornaath/batshit';
import { analysis, type AnalysisSubject, type DetailedProxyMetadata } from 'venn-api';
import { compact, first, zipObject } from 'lodash';
import { useQueries } from '@tanstack/react-query';

/**
 * Minimal proxy metadata type for bulk proxy picker components.
 * Contains only the fields actively used by UI components.
 */
export type BulkPickerDetailedProxyMetadata = Pick<
  DetailedProxyMetadata,
  | 'proxyId'
  | 'proxyName'
  | 'proxyType'
  | 'numLags'
  | 'extrapolate'
  | 'unproxiedStartDate'
  | 'unproxiedEndDate'
  | 'unproxiedFrequency'
  | 'proxiedFrequency'
  | 'fundName'
>;

/**
 * Tuple type for 0, 1, or 2 returns proxies - forces callers to narrow before accessing.
 * Only returns proxies support layering. Holdings proxies are always single.
 */
export type LayeredDetailedProxyMetadata =
  | readonly []
  | readonly [BulkPickerDetailedProxyMetadata]
  | readonly [BulkPickerDetailedProxyMetadata, BulkPickerDetailedProxyMetadata];

export const getFirstProxy = (
  tuple: LayeredDetailedProxyMetadata | undefined,
): BulkPickerDetailedProxyMetadata | undefined => tuple?.[0];
export const getSecondProxy = (
  tuple: LayeredDetailedProxyMetadata | undefined,
): BulkPickerDetailedProxyMetadata | undefined => tuple?.[1];

interface UseProxyDataQueryResult {
  returns: LayeredDetailedProxyMetadata;
  holdings: DetailedProxyMetadata | undefined;
}

/**
 * Extracts the minimal fields needed for bulk proxy picker from DetailedProxyMetadata.
 */
const toBulkPickerProxyMetadata = (proxy: DetailedProxyMetadata): BulkPickerDetailedProxyMetadata => ({
  proxyId: proxy.proxyId,
  proxyName: proxy.proxyName,
  proxyType: proxy.proxyType,
  numLags: proxy.numLags,
  extrapolate: proxy.extrapolate,
  unproxiedStartDate: proxy.unproxiedStartDate,
  unproxiedEndDate: proxy.unproxiedEndDate,
  unproxiedFrequency: proxy.unproxiedFrequency,
  proxiedFrequency: proxy.proxiedFrequency,
  fundName: proxy.fundName,
});

/**
 * Expands proxyMetaList from a single DetailedProxyMetadata into a tuple of 1 or 2 proxies.
 * Used for returns proxies which support layering (e.g., BACKFILL + INTERPOLATE).
 */
const expandReturnsProxyMetaList = (proxy: DetailedProxyMetadata): LayeredDetailedProxyMetadata => {
  const proxyMetaList =
    proxy.proxyMetaList?.filter((proxyMeta) => proxyMeta.proxyVariant === 'RETURN') ?? ([] as const);
  if (!proxyMetaList || proxyMetaList.length <= 1) {
    return [toBulkPickerProxyMetadata(proxy)] as const;
  }

  if (proxyMetaList.length === 2) {
    const firstMeta = proxyMetaList[0]!;
    const secondMeta = proxyMetaList[1]!;
    return [
      {
        proxyId: firstMeta.proxyId ?? proxy.proxyId,
        proxyName: proxy.proxyNameList?.[0] ?? proxy.proxyName,
        proxyType: firstMeta.proxyType,
        numLags: firstMeta.numLags,
        extrapolate: firstMeta.extrapolate,
        unproxiedStartDate: proxy.unproxiedStartDate,
        unproxiedEndDate: proxy.unproxiedEndDate,
        unproxiedFrequency: proxy.unproxiedFrequency,
        proxiedFrequency: proxy.proxiedFrequency,
        fundName: proxy.fundName,
      },
      {
        proxyId: secondMeta.proxyId ?? proxy.proxyId,
        proxyName: proxy.proxyNameList?.[1] ?? proxy.proxyName,
        proxyType: secondMeta.proxyType,
        numLags: secondMeta.numLags,
        extrapolate: secondMeta.extrapolate,
        unproxiedStartDate: proxy.unproxiedStartDate,
        unproxiedEndDate: proxy.unproxiedEndDate,
        unproxiedFrequency: proxy.unproxiedFrequency,
        proxiedFrequency: proxy.proxiedFrequency,
        fundName: proxy.fundName,
      },
    ] as const;
  }

  const fallbackMeta = proxyMetaList[1]!;
  return [
    toBulkPickerProxyMetadata(proxy),
    {
      proxyId: fallbackMeta.proxyId ?? proxy.proxyId,
      proxyName: proxy.proxyNameList?.[1] ?? proxy.proxyName,
      proxyType: fallbackMeta.proxyType,
      numLags: fallbackMeta.numLags,
      extrapolate: fallbackMeta.extrapolate,
      unproxiedStartDate: proxy.unproxiedStartDate,
      unproxiedEndDate: proxy.unproxiedEndDate,
      unproxiedFrequency: proxy.unproxiedFrequency,
      proxiedFrequency: proxy.proxiedFrequency,
      fundName: proxy.fundName,
    },
  ] as const;
};

/**
 * Fetches proxy metadata for a list of funds, returning both returns and holdings proxies.
 */
export const useProxyDataQuery = (
  fundIds: string[],
  proxySetId: string | undefined,
): Record<string, UseProxyDataQueryResult> => {
  const results = useQueries({ queries: fundIds.map((id) => createQuery(id, proxySetId)) });
  return zipObject(
    fundIds,
    results
      .map(({ data }) => first(data))
      .map((result) => (result ? { returns: result.returns, holdings: result.holdings } : undefined)),
  ) as Record<string, UseProxyDataQueryResult>;
};

type ProxySubject = {
  id: string;
  proxySetId: string | undefined;
};
const proxyData = create({
  fetcher: async (proxySubjects: ProxySubject[]) => {
    const result = await analysis({
      analyses: [
        {
          analysisType: 'DETAILED_PROXY_METADATA',
          relative: false,
          scenarios: [],
        },
      ],
      subjects: proxySubjects.map(
        ({ id, proxySetId }) =>
          ({
            id,
            comparisonType: 'PRIMARY',
            subjectType: 'INVESTMENT',
            proxySetId: proxySetId ?? null,
          }) as AnalysisSubject,
      ),
    });

    const allProxyMetadata = compact(result.content.analyses?.[0]?.detailedProxyMetadata?.flat() ?? []);

    const results = proxySubjects.map(({ id: fundId }) => {
      const proxiesForFund = allProxyMetadata.filter((proxy) => proxy.fundId === fundId);

      const returnsProxies = proxiesForFund.filter((proxy) => proxy.proxyVariant === 'RETURN');
      const returnsProxy = returnsProxies[0];

      const maybeHoldingsProxy = proxiesForFund.find((proxy) => proxy.proxyVariant === 'HOLDING') ?? returnsProxy;
      return {
        fundId,
        returns: returnsProxy ? expandReturnsProxyMetaList(returnsProxy) : ([] as const),
        holdings: maybeHoldingsProxy ? extractHoldingsProxy(maybeHoldingsProxy) : undefined,
      };
    });

    return results;
  },
  resolver: (proxyMetadata, { id }) => proxyMetadata.filter((datum) => datum.fundId === id),
  scheduler: windowScheduler(10),
});

const createQuery = (fundId: string, proxySetId: string | undefined) => ({
  staleTime: 5 * 60 * 1000,
  queryKey: createInvestmentProxyMetadataKey(fundId, proxySetId),
  queryFn: async () => proxyData.fetch({ id: fundId, proxySetId }),
  suspense: true,
});
